import { useEffect } from 'react';

import { FormattedMessage } from 'react-intl';
import { useRouteError } from 'react-router-dom';

import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import ErrorOutlineOutlinedIcon from '@mui/icons-material/ErrorOutlineOutlined';

// ==============================|| ROUTE ERROR (router errorElement) ||============================== //
// Without an errorElement a render crash anywhere in the tree fell through to react-router's built-in
// DEVELOPER screen ("Unexpected Application Error!" + a minified stack + "Hey developer 👋") — which is
// what students saw on the live student event page. This replaces it with something staff can act on.

/**
 * `NotFoundError` from removeChild/insertBefore means something OUTSIDE React rewrote the DOM under it —
 * in practice the browser's page translation (it wraps text nodes in <font>) or an extension. index.html
 * opts out of translation, but an extension can still do it, so say what to switch off.
 *
 * ⚠ It is a DOMException, which is NOT `instanceof Error` everywhere (jsdom, older engines) — checking
 * only Error hid the hint for the very crash it exists for.
 */
function isForeignDomMutation(error: unknown): boolean {
  return (error instanceof DOMException || error instanceof Error) && error.name === 'NotFoundError';
}

function describe(error: unknown): string {
  if (error instanceof Error) return `${error.name}: ${error.message}`;
  return String(error);
}

export default function RouteError() {
  const error = useRouteError();

  useEffect(() => {
    console.error(error); // keep the original stack for devtools / error reporting
  }, [error]);

  return (
    <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', p: 3 }}>
      <Stack spacing={2.5} sx={{ maxWidth: 520, alignItems: 'flex-start' }}>
        <ErrorOutlineOutlinedIcon color="error" sx={{ fontSize: 48 }} />
        <Typography variant="h3" component="h1">
          <FormattedMessage id="route-error-title" defaultMessage="Something went wrong" />
        </Typography>
        <Typography variant="body1" color="text.secondary">
          <FormattedMessage
            id="route-error-body"
            defaultMessage="This page hit an unexpected error. Reloading usually fixes it."
          />
        </Typography>
        {isForeignDomMutation(error) && (
          <Alert severity="info" variant="outlined">
            <FormattedMessage
              id="route-error-translate-hint"
              defaultMessage="If your browser is translating this page, or an extension is changing it, turn that off for this site and reload."
            />
          </Alert>
        )}
        <Stack direction="row" spacing={1.5}>
          <Button variant="contained" onClick={() => window.location.reload()}>
            <FormattedMessage id="route-error-reload" defaultMessage="Reload page" />
          </Button>
          {/* A full navigation, not a router link: the router's state is what just failed. The router's
              basename, because admin can be mounted under VITE_APP_BASE_NAME. */}
          <Button variant="outlined" href={import.meta.env.VITE_APP_BASE_NAME || '/'}>
            <FormattedMessage id="route-error-home" defaultMessage="Go to home page" />
          </Button>
        </Stack>
        {/* Kept visible, small: it is what a screenshot to support needs to carry. */}
        <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace', wordBreak: 'break-word' }}>
          {describe(error)}
        </Typography>
      </Stack>
    </Box>
  );
}
